import { Subject } from 'rxjs';

import { CoordinatorService } from './coordinator.service';
import { Temporal } from '@js-temporal/polyfill';
import { Project } from './project';
import type { ScriptClassInterface, ScriptHitsetInterface } from './script-types';
import { formatDate } from '@angular/common';

import * as Sentry from '@sentry/angular';

declare const mime: any;

export type MENU_ITEM = {
    title: string;
    fun: (x?: any) => void;
    context?: any;
    icon?: string;
    isDisabled?: boolean;
};

export type MENU_SEPARATOR = { separator: 'hr' | 'none'; title?: string };

export type MENU_OPTION = MENU_ITEM | MENU_SEPARATOR;
export type MENU_LS = [string, () => void, boolean][];

export type INTERVALARG = {
    years?: number;
    months?: number;
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
};

declare const CSL: any;
export class Utils$ {
    public manager: StorageManager;
    public root: FileSystemDirectoryHandle | undefined;
    refresh$ = new Subject<void>();

    markdownLib = 'https://cdnjs.cloudflare.com/ajax/libs/markdown-it/13.0.2/markdown-it.min.js';

    constructor(public coords: CoordinatorService) {
        this.manager = navigator.storage;
        this._initFileManager();
    }

    onStart() {}

    finalizeMenu(optLs: MENU_LS) {
        const ls: MENU_OPTION[] = [];
        optLs.forEach(([title, fun, isDisabled]) =>
            ls.push({
                title,
                fun,
                isDisabled,
            }),
        );
        return ls;
    }

    compileScriptFunction(data: string): Function | undefined {
        if (!data) return undefined;
        try {
            return new Function('$item', '$collection', data);
        } catch (e) {
            console.log('COMPILE SCRIPT ERROR', e);
            return undefined;
        }
    }

    normalizeString(txt: string | undefined) {
        txt = txt || '';
        return txt.replaceAll(/\s+/g, ' ').trim().toLowerCase();
    }

    htmlTextContent(txt: string) {
        const tempElement = document.createElement('div');
        tempElement.innerHTML = txt;
        return tempElement.textContent?.trim() || '';
    }

    htmlIncludesText(sText: string, html: string) {
        if (!html) return false;
        sText = this.normalizeString(sText);
        html = this.normalizeString(this.htmlTextContent(html));
        return html.includes(sText);
    }

    private async _initFileManager() {
        this.root = await this.manager.getDirectory();
    }

    trackPromise(promise: Promise<any>) {
        let isSettled = false; // Initial state, not settled

        const wrappedPromise = promise.then(
            (value) => {
                isSettled = true; // Settled as fulfilled
                return value;
            },
            (error) => {
                isSettled = true; // Settled as rejected
                throw error;
            },
        );

        return {
            promise: wrappedPromise,
            isSettled: () => isSettled,
        };
    }

    openMemowlWeb(path: string) {
        this.coords.display$.dialog$.next({
            action: 'memowl-web',
            path,
        });
    }

    guessMimetype(txt: string | URL) {
        if (txt instanceof URL) {
            txt = txt.pathname;
        }
        return mime.getType(txt) || '';
    }

    async checkUrl(url: string): Promise<boolean> {
        try {
            const response = await fetch(url, {
                method: 'HEAD',
                mode: 'no-cors',
            });
            return response.type.startsWith('opaque');
        } catch (error) {
            return false;
        }
    }

    listStartsWith<T>(longerList: T[], shorterList: T[]): boolean {
        if (shorterList.length > longerList.length) {
            return false;
        }

        for (let i = 0; i < shorterList.length; i++) {
            if (longerList[i] !== shorterList[i]) {
                return false;
            }
        }

        return true;
    }

    parseManifestLoadResponse(
        prefix: string,
        mp: Map<string, any>,
        memberLs: [string, Map<string, any>][],
    ) {
        const finalMap = new Map<string, any>();
        const uLs = Array.from(mp.entries())
            .filter((tpl) => tpl[0].startsWith(prefix))
            .map((tpl) => tpl[1]);
        if (uLs.length) finalMap.set('_current', uLs);
        for (const mTpl of memberLs) {
            const mLs = Array.from(mTpl[1].entries())
                .filter((tpl) => tpl[0].startsWith(prefix))
                .map((tpl) => tpl[1]);
            // const mLs = Array.from(mTpl[1].entries())
            //     .filter(tpl => tpl[0].startsWith(prefix))
            //     .map(tpl => Array.from(tpl[1].values()))

            mLs.length && finalMap.set(mTpl[0], mLs);
        }
        return finalMap;
    }

    highlightFirstLastAuthor(ls: string[]) {
        if (!ls.length) return;
        try {
            const h$ = this.coords.highlight$;
            if (!ls[0].includes('<')) ls[0] = h$.run(ls[0], 'author-first');
            const i = ls.length - 1;
            if (!ls[i].includes('<')) ls[i] = h$.run(ls[i], 'author-last');
        } catch (error) {}
    }

    consoleInfo({
        tag,
        payload,
        color,
        background,
        bold,
        italics,
        isGroup,
    }: {
        tag: string;
        payload?: any | any[];
        color?: string;
        background?: string;
        bold?: boolean;
        italics?: boolean;
        isGroup?: boolean;
    }) {
        const params: any = {};
        color && (params.color = color);
        background && (params.background = background);
        bold && (params.fontWeight = 'bold');
        italics && (params.fontStyle = 'italic');

        const ls: any[] = [];
        if (payload !== undefined) {
            if (Array.isArray(payload)) ls.push(...payload);
            else ls.push(payload);
        }

        (isGroup ? console.group : console.log)(
            `%c ${tag}`,
            Object.entries(params)
                .map(([key, value]) => `${key}: ${value}`)
                .join('; '),
            ...ls,
        );
    }

    normalizeSpace(
        txt: string | number | undefined,
        {
            toLower,
            toUpper,
            hyphen,
            spaceReplacement,
        }: {
            toLower?: boolean;
            toUpper?: boolean;
            hyphen?: boolean;
            spaceReplacement?: string;
        } = {},
    ) {
        txt = txt || '';
        if (typeof txt !== 'string') txt = txt.toString();
        txt = txt.replaceAll(/\s+/g, ' ').trim();
        if (hyphen) txt = txt.replaceAll(/-/g, '‐'); // Normalize hyphens normal with nonbreaking
        if (toLower) txt = txt.toLowerCase();
        else if (toUpper) txt = txt.toUpperCase();
        if (spaceReplacement) txt = txt.replaceAll(' ', spaceReplacement);
        return txt;
    }

    datepart2ISO(params: { 'date-parts': Array<[number, number, number]> }) {
        const p = params['date-parts'][0];
        const dateTime = Temporal.PlainDateTime.from({
            year: p[0],
            month: p[1] || 1,
            day: p[2] || 1,
            hour: 0,
            minute: 0,
            second: 0,
        });
        const utcDateTime = dateTime.toZonedDateTime('UTC');
        return utcDateTime.toInstant().toString().split('T')[0];
    }

    async filehandleFromPath(path: string) {
        const pathLs = path.split('/').filter((x) => x.trim());
        let dH = await navigator.storage.getDirectory();
        for (const d of pathLs) dH = await dH.getDirectoryHandle(d, { create: true });
        return dH;
    }

    dateDisplay(dt?: Temporal.Instant) {
        if (!dt) dt = Temporal.Now.instant();
        try {
            return (
                dt && dt.toLocaleString('en', { year: 'numeric', month: '2-digit', day: '2-digit' })
            );
        } catch (e) {
            return '';
        }
    }

    async writeToDisk(path: string, payload: string | Blob | Uint8Array | ArrayBuffer) {
        const pathLs = path.split('/').filter((x) => x.trim());
        const fn = pathLs.pop() || '';
        let dH = await navigator.storage.getDirectory();
        for (const d of pathLs) dH = await dH.getDirectoryHandle(d, { create: true });
        const fH = await dH.getFileHandle(fn, { create: true });

        if (fH.createWritable) {
            const writeable = await fH.createWritable();
            await writeable.write(payload);
            await writeable.close();
        } else {
            console.log('SAFARI FALLBACK');
            await this.coords.worker$.manifest.call('save-buffer', {
                path,
                payload,
            });
        }
    }

    consoleError(tag: string, e: any) {
        const stack = e.stack || '';
        const lines = stack.split('\n');
        console.error(`%c ${lines[2]} - ${tag}`, 'color: red; font-weight: bold;', e);
    }

    private _padNumber(nmb: Number, len = 2) {
        return nmb.toString().padStart(len, '0');
    }

    citeDate(
        zdt: Temporal.Instant | Temporal.ZonedDateTime,
        {
            withTime = false,
            noFormat = false,
            showSeconds = false,
        }: {
            withTime?: boolean;
            noFormat?: boolean;
            showSeconds?: boolean;
        } = {},
    ) {
        try {
            if (!zdt) {
                return '';
            }

            if (zdt instanceof Temporal.Instant) zdt = zdt.toZonedDateTimeISO(this.timezone);
            // zdt = zdt.toZonedDateTimeISO("Europe/Prague")

            const pdt = zdt.toPlainDateTime();
            const params: any = {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            };
            if (withTime) {
                params.hour = '2-digit';
                params.minute = '2-digit';
                params.hour12 = false;
                if (showSeconds) params.second = '2-digit';
            }

            let txt = pdt.toLocaleString(navigator.language, params);
            // let txt = pdt.toLocaleString("cs-CZ", params)

            // let txt = `${zdt.day}. ${monthLs[zdt.month - 1]} ${zdt.year}`
            if (!noFormat) txt = `<span class='cDate'>${txt}</span>`;
            // if (withTime) {
            //     let tm = `${this._padNumber(zdt.hour)}:${this._padNumber(zdt.minute)}`
            //     tm = noFormat ? ` - ${tm}` : ` <span class='cTime'>${tm}</span>`
            //     txt += tm
            // }
            return txt;
        } catch (e) {
            console.error('ew5489', e);
            return `${zdt}`;
        }
    }

    extractDOI(txt: string) {
        if (!txt) return null;
        const regex = /10.\d{4,9}\/[-._;()/:A-Za-z0-9]+/;
        const matches = txt.match(regex);
        return matches ? (matches[0] as string) : null;
    }

    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = crypto.getRandomValues(new Uint8Array(1))[0] % 16 | 0,
                v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    async cacheStatus(projectId: string, updatedUserId?: string) {
        const rH = await navigator.storage.getDirectory();
        const hashMp = new Map<string, string>();

        try {
            const pH = (await rH.getDirectoryHandle(projectId)) as any;
            if (!updatedUserId) await this._cacheDirectoryStatus(pH, hashMp);

            try {
                const tH = (await pH.getDirectoryHandle('_team')) as any;
                for await (const tpl of tH.entries()) {
                    const userId = tpl[0];
                    if (!updatedUserId || (updatedUserId && userId == updatedUserId))
                        await this._cacheDirectoryStatus(tpl[1], hashMp, `_team/${userId}/`);
                }
            } catch (e) {}
            return hashMp;
        } catch (e) {
            return hashMp;
        }
    }

    private async _cacheDirectoryStatus(
        dH: FileSystemDirectoryHandle,
        mp: Map<string, string>,
        prefix = '',
    ) {
        for await (const tpl of (dH as any).entries()) {
            if (tpl[1].kind === 'file' && tpl[0].startsWith('.hash_')) {
                const hnm = tpl[0];
                const hash = hnm.slice(hnm.length - 32);
                const fn = `${prefix}${hnm.slice(6, hnm.length - 33)}`;
                mp.set(fn, hash);
            }
        }
    }

    copyToClipboard(txt: string) {
        try {
            navigator.clipboard.writeText(txt);
        } catch (e) {
            console.error('Copy to clipboard', e);
        }
    }

    mailClient({ subject, body, emailLs }: { subject: string; body: string; emailLs?: string[] }) {
        subject = encodeURIComponent(subject);
        body = encodeURIComponent(body);
        const emails = (emailLs || []).join(',');
        const url = `mailto:${emails}?subject=${subject}&body=${body}`;
        window.open(url, '_mail');
    }

    async devToolsDialog() {
        this.coords.display$.dialog$.next({
            action: 'devtools',
        });
    }

    bytesToHumanReadable(numBytes: number | undefined): string {
        if (numBytes === undefined) numBytes = 0;
        const units = ['B', 'kB', 'MB', 'GB', 'TB'];
        for (const unit of units) {
            if (Math.abs(numBytes) < 1024.0) {
                return `${Number(numBytes.toFixed(1)).toString()} ${unit}`;
            }
            numBytes /= 1024.0;
        }
        return 'Size too large';
    }

    zonedDateTimeFromNumber(num: number) {
        const instant = Temporal.Instant.fromEpochSeconds(num);
        return instant.toZonedDateTimeISO(this.timezone);
    }

    nowTimeString(showSeconds = false) {
        const options = {
            hour: '2-digit' as const,
            minute: '2-digit' as const,
            hour12: false,
            ...(showSeconds && { second: '2-digit' as const }),
        };
        return new Date().toLocaleTimeString(undefined, options);
    }

    get zonedDateTimeNow() {
        const instant = Temporal.Now.instant();
        return instant.toZonedDateTimeISO('UTC');
    }

    get nowTimestamp() {
        const instant = Temporal.Now.instant();
        return instant.toString();
    }

    instant2date(inst: Temporal.Instant) {
        try {
            const zdt = inst.toZonedDateTimeISO('UTC');
            return new Date(zdt.epochMilliseconds);
        } catch (e) {
            console.error('instant2date', e);
            return null;
        }
    }

    date2instant(date: Date) {
        try {
            return Temporal.Instant.fromEpochMilliseconds(date.getTime());
        } catch (e) {
            console.error('date2instant', e);
            return null;
        }
    }

    // Normally, project dates should be in Temporal.Instant format.
    // However, there are some projects that have dates in string format, like 'dd.mm.yyyy'.
    // Once all projects are updated to use Temporal.Instant, this method can be removed.
    tryParseDate = (date: Temporal.Instant | string): Date | null => {
        let parsedDate: Date | null = null;

        if (date instanceof Temporal.Instant) {
            parsedDate = this.instant2date(date);
        }

        if (!parsedDate && typeof date === 'string') {
            parsedDate = new Date(date);

            if (isNaN(parsedDate.getTime())) {
                const [day, month, year] = date.split('.');
                parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
            }
        }

        if (!parsedDate || isNaN(parsedDate.getTime())) {
            parsedDate = null;
        }

        return parsedDate;
    };

    tryFormatDate = (date: Temporal.Instant | Date | string): string => {
        let parsedDate;

        if (date instanceof Date) {
            parsedDate = date;
        } else {
            parsedDate = this.tryParseDate(date);
        }

        if (!parsedDate) {
            return '';
        }

        return formatDate(parsedDate, 'dd/MM/yyyy', 'en');
    };

    get timezone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    futureDate(params: INTERVALARG) {
        const now = this.zonedDateTimeNow;
        return now.add(params);
    }

    async _load3data(mp: Map<string, FileSystemFileHandle[]>, type: string, userId: string) {
        const finalLs: any[] = [];
        for (const fH of mp.get(type) || [])
            try {
                const f = await fH.getFile();
                const data = await f.text();
                finalLs.push(JSON.parse(data));
            } catch (e) {
                console.error('_load3data', e, fH.name);
            }
        finalLs.forEach((x: any) => {
            try {
                x.ownerId = userId;
            } catch (e) {}
        });
        return finalLs;
    }

    async _load3databin(mp: Map<string, FileSystemFileHandle[]>, userId: string) {
        const databinLs = await this._load3data(mp, 'databin', userId);

        const databinXitemLs = await this._load3data(mp, 'databin-xitemLs', userId);

        const connectionLs = await this._load3data(mp, 'connection', userId);

        const connectionXitemIdLs = await this._load3data(mp, 'connection-xitemIdLs', userId);
        class C {
            constructor(public conn: any) {}
        }
        class D {
            xitemLs: any[] = [];
            cLs: C[] = [];
            constructor(
                public dtb: any,
                public ownerId: string,
            ) {}
        }

        const dMp = new Map<string, D>();
        const cMp = new Map<string, C>();
        databinLs.forEach((x: any) => dMp.set(x.id, new D(x, userId)));
        databinXitemLs.forEach((x: any) => {
            const dtb = dMp.get(x.id);
            if (dtb) dtb.xitemLs = x.xitemLs;
        });
        connectionLs.forEach((x: any) => {
            const dtb = dMp.get(x._databinId);
            if (dtb) {
                const c = new C(x);
                cMp.set(x.id, c);
                dtb.cLs.push(c);
            }
        });
        connectionXitemIdLs.forEach((x: any) => {
            const c = cMp.get(x.id);
            if (c) c.conn.xitemIdLs = x.xitemIdLs;
        });
        return Array.from(dMp.values());
    }

    async load3(mp: Map<string, FileSystemFileHandle[]>, userId: string, isOwner = false) {
        console.log('MP load3', mp);

        const isCurrentUser = this.coords.user$.currentId === userId;
        let pr: Project | undefined = undefined;
        if (isOwner) {
            pr = this.coords.project$.load3((await this._load3data(mp, 'project', userId))[0]);
            this.coords.keyword$.load3((await this._load3data(mp, 'keyword', userId))[0]);
            this.coords.highlight$.load3((await this._load3data(mp, 'style', userId))[0]);

            this.coords.databin$.load3(await this._load3databin(mp, userId));

            this.coords.dataset$.load3((await this._load3data(mp, 'dataset-xitemLs', userId))[0]);

            this.coords.script$.load3((await this._load3data(mp, 'script', userId))[0]);

            this.coords.binaryTree$.load3(await this._load3data(mp, 'bt', userId));
            this.coords.valueItem$.load3(await this._load3data(mp, 'value-item-template', userId));
            this.coords.condition$.load3(await this._load3data(mp, 'condition-template', userId));
        }

        if (isCurrentUser) this.coords.pin$.load3(await this._load3data(mp, 'pin', userId));

        this.coords.autouser$.load3(await this._load3data(mp, 'auto-user', userId));
        this.coords.binaryTree$.load3storage(await this._load3data(mp, 'bt-storage', userId));

        // this.coords.values$.load3(await this._load3data(mp, 'values', userId));
        this.coords.valueItem$.load3storage(
            await this._load3data(mp, 'value-item-storage', userId),
        );
        this.coords.condition$.load3storage(await this._load3data(mp, 'condition-storage', userId));

        this.coords.comment$.load3((await this._load3data(mp, 'comment', userId))[0]);

        this.coords.searchArchive$.load3((await this._load3data(mp, 'search-archive', userId))[0]);

        this.coords.view$.load3(await this._load3data(mp, 'view', userId));
        this.coords.notepad$.load3(await this._load3data(mp, 'notepad', userId));

        this.coords.attachment$.load3((await this._load3data(mp, 'attachment', userId))[0]);

        this.coords.stage$.afterProjectLoaded(isOwner, pr);
        this.coords.display$.markForCheck();
        setTimeout(() => this.coords.display$.closeDialog(), 300);
        return pr;
    }

    async filehandleMap(dH: FileSystemDirectoryHandle) {
        const mp = new Map<string, FileSystemFileHandle[]>();

        const fhLs: FileSystemFileHandle[] = [];

        for await (const e of (dH as any).values()) {
            if (e.kind == 'file' && (e.name.startsWith('.hash') || !e.name.startsWith('.')))
                fhLs.push(e);
        }

        fhLs.forEach((fH) => {
            if (fH.name.startsWith('.')) return;
            const type = fH.name.slice(0, fH.name.indexOf('_'));
            let ls = mp.get(type);
            if (!ls) {
                ls = [];
                mp.set(type, ls);
            }
            ls.push(fH);
        });
        return mp;
    }

    uniqueTitle(title: string, exists: string[] | Set<string>) {
        const s: Set<string> = new Set(exists);
        const regex = /\(\s*(\d+)\s*\)$/;
        while (s.has(title)) {
            const match = title.match(regex);
            if (match) {
                const num = parseInt(match[1], 10) + 1;
                title = title.replace(regex, `(${num})`);
            } else title += ' (1)';
        }
        return title;
    }

    async wait(seconds: number) {
        await new Promise((resolve) => {
            setTimeout(() => resolve(() => {}), seconds * 1000);
        });
    }

    quilToolbarOptions() {
        return [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'], // toggled buttons
            ['blockquote', 'code-block'],
            ['formula'],
            [{ size: ['small', false, 'large', 'huge'] }],
            // [{ 'header': 1 }, { 'header': 2 }],               // custom button values
            [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
            [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
            [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
            // [{ 'direction': 'rtl' }],                         // text direction

            // [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown

            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            // [{ 'font': [] }],
            [{ align: [] }],

            ['clean'], // remove formatting button
        ];
    }

    scroll(tp: 'top' | 'bottom') {
        switch (tp) {
            case 'top':
                window.scrollTo(0, 0);
                break;
            case 'bottom':
                window.scrollTo(0, document.body.scrollHeight);
                break;
        }
    }

    toJSONready(input: any): any {
        if (input instanceof Temporal.PlainDate) {
            return input.toString();
        }

        if (input instanceof Temporal.PlainTime) {
            return input.toString();
        }

        if (input instanceof Temporal.PlainDateTime) {
            return input.toString();
        }

        if (input instanceof Temporal.ZonedDateTime) {
            return input.toString();
        }

        if (input instanceof Date) {
            return input.toISOString();
        }

        if (Array.isArray(input)) {
            return input.map((item: any) => this.toJSONready(item));
        }

        if (input.toJSONready) {
            return input.toJSONready();
        }

        if (!(input instanceof Map)) {
            return input;
        }

        const obj: Record<string, any> = {};
        for (const [key, value] of input.entries()) {
            obj[key] = this.toJSONready(value);
        }
        return obj;
    }

    objectsToMaps(input: any) {
        if (typeof input === 'object' && input !== null && !(input instanceof Array)) {
            const mp = new Map<string, any>();
            for (const [key, value] of Object.entries(input)) {
                mp.set(key, this.objectsToMaps(value));
            }
            return mp;
        }

        return input;
    }

    containsNetworkRequests(txt: string) {
        const patterns = [/fetch\s*\(/, /new\s+XMLHttpRequest/, /navigator\.sendBeacon\s*\(/];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsDOMAccess(txt: string) {
        const patterns = [
            /document\.querySelector/,
            /document\.getElementById/,
            /document\.createElement/,
        ];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsEvalOrFunctionConstructor(txt: string) {
        const patterns = [/eval\s*\(/, /new\s+Function\s*\(/];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsGlobalAccess(txt: string) {
        const patterns = [/window\./, /globalThis\./];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsExternalScriptLoading(txt: string) {
        const patterns = [/<script/, /importScripts\s*\(/];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsStorageAccess(txt: string) {
        const patterns = [/document\.cookie/, /localStorage\./, /sessionStorage\./];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsRedirection(txt: string) {
        const patterns = [/location\.href/, /location\.assign/, /window\.location/];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsDynamicImports(txt: string) {
        const patterns = [/import\s*\(/];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsWebSocket(txt: string) {
        const patterns = [/new\s+WebSocket/];
        return patterns.some((pattern) => pattern.test(txt));
    }

    containsEventListener(txt: string) {
        const patterns = [/\.addEventListener\s*\(/];
        return patterns.some((pattern) => pattern.test(txt));
    }

    isFunctionBodySafe(fun: Function) {
        const txt = this.funBody(fun);

        return (
            !this.containsNetworkRequests(txt) &&
            !this.containsDOMAccess(txt) &&
            !this.containsEvalOrFunctionConstructor(txt) &&
            !this.containsGlobalAccess(txt) &&
            !this.containsExternalScriptLoading(txt) &&
            !this.containsStorageAccess(txt) &&
            !this.containsRedirection(txt) &&
            !this.containsDynamicImports(txt) &&
            !this.containsWebSocket(txt) &&
            !this.containsEventListener(txt)
        );
    }

    funBody(fun: Function | undefined) {
        if (!fun) return '';
        let txt = fun.toString();
        txt = txt.substring(txt.indexOf('{') + 1, txt.lastIndexOf('}'));
        return txt;
    }

    isCurrentHostname(src: string) {
        try {
            const url = new URL(src);
            return url.hostname === location.hostname;
        } catch (e) {
            return true;
        }
    }

    hasScriptClass(arg: any): arg is ScriptClassInterface {
        return arg.scriptClass;
    }

    hasHitset(arg: any): arg is ScriptHitsetInterface {
        return arg.hitset;
    }

    // isScriptClassDisabled(arg: SCRIPT_TYPE) {
    //     return arg.selectionRole === 'hide' || arg.selectionRole === 'off';
    // }

    markdownTable(ls: string[][]) {
        ls.splice(1, 0, (Array(ls[0].length) as string[]).fill('---'));
        const rowLs = ls.map((lineLs) => `| ${lineLs.join(' | ')} |`);
        return rowLs.map((x) => x.trim()).join('\n');
    }

    equal(arg1: any, arg2: any) {
        if (typeof arg1 !== typeof arg2) return false;
        if (Array.isArray(arg1)) {
            if (arg1.length !== arg2.length) return false;
            for (let i = 0; i < arg1.length; i++) {
                if (!this.equal(arg1[i], arg2[i])) return false;
            }
            return true;
        }

        return arg1 === arg2;
    }

    escapeHTML(txt: string, isAttr = false) {
        const regexp = isAttr ? /[<>&'"]/g : /[<>&]/g;
        return txt.replace(regexp, function (c) {
            switch (c) {
                case '<':
                    return '&lt;';
                case '>':
                    return '&gt;';
                case '&':
                    return '&amp;';
                case '"':
                    return '&quot;';
                case "'":
                    return '&apos;';
                default:
                    return c;
            }
        });
    }

    logError(error: Error | string) {
        const exception = error instanceof Error ? error : new Error(error);
        Sentry.captureException(exception);
    }

    logInfo(message: string) {
        Sentry.captureMessage(`Info: ${message}`, { level: 'info' });
    }

    logWarning(message: string) {
        Sentry.captureMessage(`Warning: ${message}`, { level: 'warning' });
    }
}
